'use client';

import { useCallback, useEffect, useRef } from 'react';
import { useInView } from 'react-intersection-observer';
import { VIDEO_VARIANTS } from '@workspace/constants';
import { VideoVariant } from '@workspace/constants/video-image.constant';
import { useVimeoPlayer } from './useVimeoPlayer';
import { VimeoEmbedParameters } from '@vimeo/player';
import { backgroundVimeoPlayerOptions, VIDEO_VARIANTS as VIDEO_TYPES } from '@workspace/constants';
import { imageVideoWithTextVimeoOptions } from '@workspace/constants/video-image.constant';

interface UseVimeoPlayerWithAutoPlayOptions {
  video?: string;
  variant: VideoVariant;
  isBackgroundVideo: boolean;
  containerRef: React.RefObject<HTMLDivElement | null>;
  vimeoPlayerOptions?: Omit<VimeoEmbedParameters, 'id'>;
}

export function useVimeoPlayerWithAutoPlay({
  video,
  variant,
  isBackgroundVideo,
  containerRef,
  vimeoPlayerOptions,
}: UseVimeoPlayerWithAutoPlayOptions) {
  const { ref: wrapperRef, inView } = useInView();
  const hasAutoPlayedRef = useRef(false);
  const userPausedRef = useRef(false);

  const {
    isPlaying,
    duration,
    play: originalPlay,
    pause,
    isReady,
    reset,
    aspectRatio,
  } = useVimeoPlayer(video, containerRef, {
    ...(isBackgroundVideo &&
      (variant === VIDEO_TYPES.ImageVideoWithText ? imageVideoWithTextVimeoOptions : backgroundVimeoPlayerOptions)),
    ...vimeoPlayerOptions,
  });

  // Custom play function that waits for player to be ready
  const play = useCallback(async () => {
    if (!isReady) {
      const checkReady = () => {
        if (isReady) {
          originalPlay();
        } else {
          setTimeout(checkReady, 100);
        }
      };
      checkReady();
      return;
    }
    await originalPlay();
  }, [isReady, originalPlay]);

  // Track when user manually pauses (for ImageVideoWithText variant)
  useEffect(() => {
    if (variant === VIDEO_VARIANTS.ImageVideoWithText && isBackgroundVideo) {
      if (hasAutoPlayedRef.current && inView && !isPlaying && isReady) {
        userPausedRef.current = true;
      }
    }
  }, [isPlaying, variant, isBackgroundVideo, inView, isReady]);

  // Handle auto-play and reset
  useEffect(() => {
    // Auto play background videos when first coming into view (only if user hasn't manually paused)
    if (isBackgroundVideo && inView && isReady && !hasAutoPlayedRef.current && !userPausedRef.current) {
      play();
      hasAutoPlayedRef.current = true;
      return;
    }

    // Reset flags and video when going out of view (for all variants)
    if (!inView) {
      hasAutoPlayedRef.current = false;
      userPausedRef.current = false;
      reset();
    }
  }, [inView, isReady, isBackgroundVideo, play, reset]);

  return {
    isPlaying,
    duration,
    play,
    pause,
    isReady,
    reset,
    wrapperRef,
    inView,
    aspectRatio,
  };
}
